var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { version } from "@hyper-hyper-space/hhs3_mvt";
import { splitTableRef } from "@hyper-hyper-space/hhs3_rdb";
import { bind, execute, parseScript, } from "@hyper-hyper-space/hhs3_rdb_lang";
import { KeyPassphraseRequiredError } from "./session.js";
import { suggestAuthorsForBindFailure, suggestAuthorsForFailure } from "./authz_suggest.js";
import { tryAuthSignRetry, tryBindAuthorRetry } from "./sign_retry.js";
import { extractRefUpdateTrigger, propagateRefUpdates } from "./ref_auto_update.js";
import { frontierForScope, hashScopeForVersionScope, resolveVersionMember, } from "./version.js";
import { rootCtx } from "./root_context.js";
export function createBindContext(session) {
    const ctx = rootCtx(session);
    return {
        resolveSchema: (ref) => session.workspace.roots.resolveSchema(ref, ctx),
        resolveGroup: (ref) => session.workspace.roots.resolveGroup(ref, ctx),
        resolveDatabase: (ref) => session.workspace.roots.resolveDatabase(ref, ctx),
        resolveTable: (ref) => session.workspace.roots.resolveTable(ref, ctx),
        resolveDefaultGroup: () => __awaiter(this, void 0, void 0, function* () {
            return session.currentGroup === undefined
                ? undefined
                : {
                    kind: 'name',
                    text: session.currentGroup,
                    parts: [session.currentGroup],
                    span: { start: 0, end: session.currentGroup.length, line: 1, column: 1 },
                };
        }),
        resolveHash: (ref, scope) => session.workspace.roots.resolveHash(ref, scope),
        resolveRowId: (ref, table, at, from) => resolveRowIdPrefix(ref.prefix, table, at, from),
        resolveFkRowId: (prefix, sourceTable, column, at, from) => resolveFkRowId(prefix, sourceTable, column, at, from),
        resolveVersion: (expr, scope) => resolveVersionExpr(session, expr, scope),
        resolveDefaultView: () => __awaiter(this, void 0, void 0, function* () { return session.defaultView; }),
        resolveVariable: (name) => session.resolveVariable(name),
        resolvePublicKey: (labelOrPrefix) => session.resolvePublicKey(labelOrPrefix),
        resolveLogTarget: (ref) => session.workspace.roots.resolveLogTarget(ref, ctx),
        currentAuthor: () => session.currentAuthor(),
        resolveAuthor: (ref) => session.resolveAuthor(ref),
        createUuid: () => session.createUuid(),
        createSeed: (kind, name) => session.createSeed(kind, name),
    };
}
export function executeText(session, text, options) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const parsed = parseScript(text);
        if (!parsed.ok)
            throw new LanguageError(parsed.diagnostics);
        const results = [];
        const context = createBindContext(session);
        for (const statement of parsed.value.statements) {
            let bound = yield bind(statement, context);
            if (!bound.ok) {
                const retried = yield tryBindAuthorRetry(session, statement, bound.diagnostics, context, options);
                if (retried !== undefined) {
                    bound = { ok: true, value: retried };
                }
                else {
                    const hint = yield suggestAuthorsForBindFailure(session, statement, bound.diagnostics, context);
                    throw new LanguageError(bound.diagnostics, hint === undefined ? [] : [hint]);
                }
            }
            const executed = yield execute(bound.value);
            let effectiveBound = bound.value;
            let result;
            if (!executed.ok) {
                const retried = yield tryAuthSignRetry(session, bound.value, executed.diagnostics, options);
                if (retried !== undefined) {
                    effectiveBound = retried.bound;
                    result = retried.result;
                }
                else {
                    const hint = yield suggestAuthorsForFailure(session, bound.value, executed.diagnostics);
                    throw new LanguageError(executed.diagnostics, hint === undefined ? [] : [hint]);
                }
            }
            else {
                result = executed.value;
            }
            if (result.kind === 'create-plan') {
                const object = yield session.workspace.createRoot(result.plan);
                if (result.plan.kind === 'create-database')
                    session.setCurrentDatabase(object.getId());
                if (result.plan.kind === 'create-tablegroup')
                    session.setCurrentGroup(object.getId());
            }
            else if (result.kind === 'set-view') {
                session.setDefaultView({
                    at: yield resolveVersionExpr(session, result.at, { kind: 'group', id: (_a = session.currentGroup) !== null && _a !== void 0 ? _a : '', group: undefined }),
                    from: result.from === undefined
                        ? undefined
                        : yield resolveVersionExpr(session, result.from, { kind: 'group', id: (_b = session.currentGroup) !== null && _b !== void 0 ? _b : '', group: undefined }),
                });
            }
            const item = { result };
            const trigger = extractRefUpdateTrigger(effectiveBound);
            if (session.refAutoUpdate !== 'off' && trigger !== undefined) {
                item.events = yield propagateRefUpdates(session, trigger.sourceGroupId, trigger.author, options);
            }
            results.push(item);
        }
        return { results };
    });
}
export class LanguageError extends Error {
    constructor(diagnostics, hints = []) {
        super(diagnostics.map((d) => `${d.code}: ${d.message}`).join('\n'));
        this.diagnostics = diagnostics;
        this.hints = hints;
    }
}
const KEY_PASSPHRASE_REQUIRED = /^Key '([^']+)' is not unlocked$/;
export function keyPassphraseRequiredFromError(e) {
    if (e instanceof KeyPassphraseRequiredError)
        return e;
    if (e instanceof LanguageError) {
        for (const diagnostic of e.diagnostics) {
            const match = KEY_PASSPHRASE_REQUIRED.exec(diagnostic.message);
            if (match !== null)
                return new KeyPassphraseRequiredError(match[1]);
        }
    }
    return undefined;
}
function resolveVersionExpr(session, expr, scope) {
    return __awaiter(this, void 0, void 0, function* () {
        if (expr === undefined) {
            if (session.defaultView !== undefined)
                return session.defaultView.at;
            return frontierForScope(scope);
        }
        if (expr.kind === 'latest')
            return frontierForScope(scope);
        const hashScope = hashScopeForVersionScope(scope);
        if (expr.kind === 'hash') {
            const hash = yield session.workspace.roots.resolveHash(expr.hash, hashScope);
            return version(hash);
        }
        const hashes = [];
        for (const member of expr.members) {
            hashes.push(yield resolveVersionMember(session, member, hashScope));
        }
        return version(...hashes);
    });
}
export function resolveRowIdPrefix(prefix, table, at, from) {
    return __awaiter(this, void 0, void 0, function* () {
        const view = yield table.table.getView(at, from !== null && from !== void 0 ? from : at);
        const tableName = `${table.groupId}.${table.tableName}`;
        return matchRowIdPrefix(prefix, yield view.liveRowIds(), tableName);
    });
}
export function resolveFkRowId(prefix, sourceTable, column, at, from) {
    return __awaiter(this, void 0, void 0, function* () {
        const fromVersion = from !== null && from !== void 0 ? from : at;
        const groupView = yield sourceTable.group.getView(at, fromVersion);
        const schemaView = groupView.getSchemaView();
        const targetRef = schemaView.getFKs(sourceTable.tableName)[column];
        if (targetRef === undefined) {
            throw new Error(`Column '${column}' is not a REFERENCES column`);
        }
        const [groupName, targetTable] = splitTableRef(targetRef);
        if (groupName === undefined) {
            const view = yield groupView.getTableView(targetTable);
            const tableName = `${sourceTable.groupId}.${targetTable}`;
            return matchRowIdPrefix(prefix, yield view.liveRowIds(), tableName);
        }
        const fkGroup = sourceTable.group;
        const view = yield fkGroup.resolveForeignTableView(groupName, targetTable, at, fromVersion);
        if (view === undefined) {
            throw new Error(`Unknown foreign table '${groupName}.${targetTable}' for FK column '${column}'`);
        }
        const tableName = `${sourceTable.groupId}.${groupName}.${targetTable}`;
        return matchRowIdPrefix(prefix, yield view.liveRowIds(), tableName);
    });
}
function matchRowIdPrefix(prefix, rowIds, tableName) {
    const matches = rowIds.filter((rowId) => rowId.startsWith(prefix));
    if (matches.length === 1)
        return matches[0];
    if (matches.length === 0)
        throw new Error(`Unknown rowId prefix '#${prefix}' in ${tableName}`);
    const examples = matches.slice(0, 5).map((rowId) => `#${rowId}`).join(', ');
    throw new Error(`Ambiguous rowId prefix '#${prefix}' in ${tableName}: ${examples}`);
}
